import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import de from './locales/de.json';
import en from './locales/en.json';
import es from './locales/es.json';

export const supportedLngs = ['de', 'en', 'es'] as const;
export type SupportedLanguage = typeof supportedLngs[number];
export const fallbackLng = 'de';

function detectLanguageFromBrowser(): string {
  if (typeof navigator === 'undefined') return fallbackLng;
  const preferred = navigator.languages
    ? Array.from(navigator.languages)
    : [navigator.language];
  for (const lang of preferred) {
    const code = (lang || '').slice(0, 2).toLowerCase();
    if (supportedLngs.includes(code as SupportedLanguage)) return code;
  }
  const primary = (navigator.language || '').slice(0, 2).toLowerCase();
  if (supportedLngs.includes(primary as SupportedLanguage)) return primary;
  return fallbackLng;
}

/**
 * Applies the language from the Teams SDK context (context.app.locale).
 * Should be called after `microsoftTeams.app.getContext()` resolves.
 * The Teams locale is e.g. "de-de", "en-us", "en-gb".
 */
export function applyTeamsLocale(teamsLocale: string | undefined): void {
  if (!teamsLocale) return;
  const code = teamsLocale.slice(0, 2).toLowerCase();
  if (supportedLngs.includes(code as SupportedLanguage) && code !== i18n.language) {
    console.log(`[i18n] Switching language from "${i18n.language}" to "${code}" (Teams locale: "${teamsLocale}")`);
    i18n.changeLanguage(code);
  }
}

i18n.use(initReactI18next).init({
  resources: {
    de: { common: de },
    en: { common: en },
    es: { common: es },
  },
  defaultNS: 'common',
  fallbackLng,
  supportedLngs: [...supportedLngs],
  lng: detectLanguageFromBrowser(),
  interpolation: {
    escapeValue: false,
  },
});

i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lng;
  }
});

// Set initial html lang
if (typeof document !== 'undefined' && document.documentElement) {
  document.documentElement.lang = i18n.language;
}

export default i18n;
